import { OverlayRef } from '@angular/cdk/overlay';
import {
  ChangeDetectionStrategy,
  Component,
  HostBinding,
  HostListener,
  Input,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { NavigationStart, Router } from '@angular/router';
import { slideInFromRight } from '@x/common/animations/animations';
import { CartOverlayService } from '@x/ecommerce-shop/app/core/order/services/cart-overlay.service';
import { IShopOrderItem, TShopAssociationWithProducts } from '@x/ecommerce/shop-client';
import { BehaviorSubject, Subscription, combineLatest } from 'rxjs';
import { filter, startWith } from 'rxjs/operators';

@Component({
  selector: 'x-add-to-cart-notification-overlay',
  templateUrl: './add-to-cart-notification-overlay.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'x-add-to-cart-overlay' },
  animations: [slideInFromRight],
})
export class AddToCartNotificationOverlayComponent implements OnInit, OnDestroy {
  private subscription = new Subscription();

  @HostBinding('@slideInFromRight')
  slideInFromRight: string;

  @HostListener('mouseenter') pauseTimer() {
    this.clearTimer();
  }

  @HostListener('mouseleave') resumeTimer() {
    this.startTimer(
      this._closeNotificationTime - (this.loaderWidth$.value / 100) * this._closeNotificationTime,
    );
  }

  @Input() orderItem: IShopOrderItem;
  @Input() association: TShopAssociationWithProducts;
  @Input() overlayRef: OverlayRef;

  private _closeNotificationTime: number = 2000;
  @Input() set closeNotificationTime(time: number) {
    this._closeNotificationTime = time;
    this.restartTimer();
  }

  private interval: any;
  loaderWidth$ = new BehaviorSubject<number>(0);

  closeOverlay$ = combineLatest([
    this.miniCartService.cartIsOpen$,
    this.router.events.pipe(
      filter((event) => event instanceof NavigationStart),
      startWith(null),
    ),
  ]);

  constructor(
    private miniCartService: CartOverlayService,
    private router: Router,
  ) {}

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  ngOnInit(): void {
    this.subscription.add(
      this.closeOverlay$.subscribe(([isOpen, navigationEvent]) => {
        if (isOpen || navigationEvent) {
          this.closeOverlay();
        }
      }),
    );
  }

  restartTimer() {
    this.startTimer(this._closeNotificationTime);
  }

  startTimer(timeLeft: number) {
    this.clearTimer();

    const updateInterval = 100;
    const totalUpdates = timeLeft / updateInterval;
    const incrementPerUpdate = 100 / (this._closeNotificationTime / updateInterval);
    let updatesCount = 0;

    this.interval = setInterval(() => {
      updatesCount++;
      let currentValue = this.loaderWidth$.value + incrementPerUpdate;
      if (currentValue >= 100 || updatesCount >= totalUpdates) {
        currentValue = 100;
        this.closeOverlay();
      }
      this.loaderWidth$.next(currentValue);
    }, updateInterval);
  }

  clearTimer() {
    if (this.interval) {
      clearInterval(this.interval);
    }
  }

  closeOverlay() {
    if (this.overlayRef) {
      this.overlayRef.detach();
      this.clearTimer();
    }
  }

  viewBag() {
    if (this.overlayRef) {
      this.miniCartService.openCart();
      this.closeOverlay();
    }
  }
}
